import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getManualAllocationDetail,
  getManualAllocationPayments,
  saveManualAllocations,
  confirmManualAllocations,
  rejectManualPayment,
  paymentReceiptUrl,
  emailPaymentReceipt,
  manualAllocationEvidenceUrl,
  type ManualAllocationDetail,
  type ManualAllocationListItem,
} from '@urbanik/api-client';
import { CheckCircle2, ListChecks, Mail, Share2 } from 'lucide-react';
import { CanonicalModal, DataTable, EmptyState, TextAreaField, type DataColumn } from '@urbanik/ui';
import { apiBase } from './api-base';
import { fetchProtectedDocument, openProtectedDocument } from './protected-document';
import { sharePaymentReceiptViaWhatsApp } from './payment-receipt-sharing';

const cents = (value: string) => Math.round(Number(value || 0) * 100);
const money = (value: number) =>
  new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' }).format(value / 100);
const longDate = (value: string) => {
  const parsed = new Date(`${value}T12:00:00-05:00`);
  return Number.isNaN(parsed.getTime())
    ? 'Fecha no disponible'
    : new Intl.DateTimeFormat('es-EC', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'America/Guayaquil',
      }).format(parsed);
};
const longDateTime = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? 'Fecha no disponible'
    : new Intl.DateTimeFormat('es-EC', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Guayaquil',
      }).format(parsed);
};
export function ManualPaymentAllocation() {
  const approvalInFlight = useRef<string | null>(null);
  const [items, setItems] = useState<ManualAllocationListItem[]>([]),
    [detail, setDetail] = useState<ManualAllocationDetail | null>(null),
    [values, setValues] = useState<Record<string, string>>({}),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [success, setSuccess] = useState(''),
    [deliveryBusy, setDeliveryBusy] = useState<'whatsapp' | 'email' | null>(null),
    [deliveryMessage, setDeliveryMessage] = useState(''),
    [confirmOpen, setConfirmOpen] = useState(false),
    [rejectOpen, setRejectOpen] = useState(false),
    [rejectReason, setRejectReason] = useState(''),
    [approvalObservation, setApprovalObservation] = useState(''),
    [evidencePreviewUrl, setEvidencePreviewUrl] = useState<string | null>(null),
    [evidencePreviewError, setEvidencePreviewError] = useState(''),
    [evidenceAttempt, setEvidenceAttempt] = useState(0),
    [receipt, setReceipt] = useState<{
      id: string;
      number: string;
      memberId: string;
      memberName: string;
      reference: string;
      appliedAmount: string;
      creditAmount: string;
      allocations: {
        period: string;
        conceptName: string;
        appliedAmount: string;
        remainingAmount: string;
        status: string;
      }[];
    } | null>(null);
  const refreshItems = useCallback(() => {
    return getManualAllocationPayments(apiBase)
      .then((result) => setItems(result.items))
      .catch((reason) => setError((reason as Error).message));
  }, []);
  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refreshItems();
    };
    void refreshItems();
    const timer = window.setInterval(() => void refreshItems(), 5_000);
    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    window.addEventListener('urbanik:refresh-admin-payments', refreshItems);
    window.addEventListener('urbanik:payment-draft-submitted', refreshItems);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.removeEventListener('urbanik:refresh-admin-payments', refreshItems);
      window.removeEventListener('urbanik:payment-draft-submitted', refreshItems);
    };
  }, [refreshItems]);
  useEffect(() => {
    const paymentId = detail?.payment.id;
    setEvidencePreviewUrl(null);
    setEvidencePreviewError('');
    if (!paymentId) return;
    const controller = new AbortController();
    let objectUrl = '';
    void fetchProtectedDocument(
      manualAllocationEvidenceUrl(apiBase, paymentId),
      'comprobante',
      controller.signal,
    )
      .then(({ blob }) => {
        objectUrl = URL.createObjectURL(blob);
        setEvidencePreviewUrl(objectUrl);
      })
      .catch((failure) => {
        if (controller.signal.aborted) return;
        setEvidencePreviewError(
          failure instanceof Error ? failure.message : 'No se pudo cargar el comprobante.',
        );
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [detail?.payment.id, evidenceAttempt]);
  const select = async (id: string) => {
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const next = await getManualAllocationDetail(apiBase, id);
      setDetail(next);
      const saved = next.obligations.filter((o) => cents(o.allocatedAmount) > 0);
      if (saved.length) setValues(Object.fromEntries(saved.map((o) => [o.id, o.allocatedAmount])));
      else
        setValues(
          Object.fromEntries(
            next.obligations
              .filter((row) => cents(row.suggestedAmount) > 0)
              .map((row) => [row.id, row.suggestedAmount]),
          ),
        );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const restoreSuggestion = () => {
    if (detail)
      setValues(
        Object.fromEntries(
          detail.obligations
            .filter((row) => cents(row.suggestedAmount) > 0)
            .map((row) => [row.id, row.suggestedAmount]),
        ),
      );
  };
  const allocated = useMemo(
      () => Object.values(values).reduce((sum, value) => sum + cents(value), 0),
      [values],
    ),
    payment = cents(detail?.payment.amount ?? '0'),
    remaining = payment - allocated;
  const toggle = (obligation: { id: string; outstandingAmount: string }) =>
    setValues((current) => {
      const next = { ...current };
      if (cents(next[obligation.id] ?? '0') > 0) {
        delete next[obligation.id];
        return next;
      }
      const used = Object.entries(next)
          .filter(([id]) => id !== obligation.id)
          .reduce((sum, [, value]) => sum + cents(value), 0),
        amount = Math.min(Math.max(payment - used, 0), cents(obligation.outstandingAmount));
      if (amount) next[obligation.id] = (amount / 100).toFixed(2);
      return next;
    });
  const save = async () => {
    if (!detail) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const allocations = Object.entries(values)
        .filter(([, v]) => cents(v) > 0)
        .map(([obligationId, amount]) => ({ obligationId, amount: Number(amount).toFixed(2) }));
      const result = await saveManualAllocations(apiBase, detail.payment.id, allocations);
      const complete = detail.obligations.filter(
        (row) => cents(values[row.id] ?? '0') === cents(row.outstandingAmount),
      ).length;
      const partialCount = detail.obligations.filter((row) => {
        const amount = cents(values[row.id] ?? '0');
        return amount > 0 && amount < cents(row.outstandingAmount);
      }).length;
      setSuccess(
        `Propuesta guardada correctamente: ${complete} cuota${complete === 1 ? '' : 's'} completa${complete === 1 ? '' : 's'} y ${partialCount} abono${partialCount === 1 ? '' : 's'} parcial${partialCount === 1 ? '' : 'es'}. Total distribuido USD ${result.allocatedAmount}; disponible USD ${result.remainingAmount}. Todavía no se modificaron los saldos.`,
      );
      setDetail(null);
      setValues({});
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const reject = async () => {
    if (!detail) return;
    if (rejectReason.trim().length < 5) {
      setError('Explica el motivo del rechazo con al menos 5 caracteres.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await rejectManualPayment(apiBase, detail.payment.id, rejectReason);
      setItems((current) => current.filter((item) => item.id !== detail.payment.id));
      setSuccess(
        `Comprobante ${detail.payment.reference} rechazado. No se modificaron cuotas ni saldos.`,
      );
      setDetail(null);
      setValues({});
      setRejectOpen(false);
      setRejectReason('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const approve = async () => {
    if (!detail || approvalInFlight.current === detail.payment.id) return;
    const approvedPayment = detail.payment;
    approvalInFlight.current = approvedPayment.id;
    setConfirmOpen(false);
    setBusy(true);
    setError('');
    try {
      const allocations = Object.entries(values)
        .filter(([, v]) => cents(v) > 0)
        .map(([obligationId, amount]) => ({ obligationId, amount: Number(amount).toFixed(2) }));
      await saveManualAllocations(apiBase, detail.payment.id, allocations);
      const result = await confirmManualAllocations(
        apiBase,
        detail.payment.id,
        approvalObservation,
      );
      setReceipt({
        id: approvedPayment.id,
        number: result.receiptNumber,
        memberId: approvedPayment.memberId,
        memberName: approvedPayment.memberName,
        reference: approvedPayment.reference,
        appliedAmount: result.appliedAmount,
        creditAmount: result.creditAmount,
        allocations: result.allocations,
      });
      setDeliveryBusy('email');
      setDeliveryMessage('');
      void emailPaymentReceipt(apiBase, approvedPayment.id)
        .then((delivery) => setDeliveryMessage(`Recibo enviado a ${delivery.recipient}.`))
        .catch((failure) =>
          setDeliveryMessage(
            failure instanceof Error
              ? failure.message
              : 'El pago fue aprobado, pero no se pudo enviar el recibo por correo.',
          ),
        )
        .finally(() => setDeliveryBusy(null));
      setSuccess(
        `Comprobante aprobado. Recibo ${result.receiptNumber}: ${result.allocations.length} aplicaciones por USD ${result.appliedAmount}${cents(result.creditAmount) > 0 ? ` y crédito disponible separado de USD ${result.creditAmount}` : ''}.`,
      );
      setItems((current) => current.filter((item) => item.id !== approvedPayment.id));
      setDetail(null);
      setValues({});
      setApprovalObservation('');
      window.dispatchEvent(
        new CustomEvent('urbanik:payment-approved', { detail: approvedPayment.id }),
      );
    } catch (e) {
      setError((e as Error).message);
      setConfirmOpen(false);
    } finally {
      approvalInFlight.current = null;
      setBusy(false);
    }
  };
  const paymentColumns: readonly DataColumn<ManualAllocationListItem>[] = [
    {
      id: 'member',
      header: 'Socio',
      accessor: (r) => r.memberName,
      className: 'manual-payment-col-member',
    },
    {
      id: 'slots',
      header: 'Cupo(s)',
      accessor: (r) => r.slotNumbers.join(', ') || 'Sin cupo asignado',
      className: 'manual-payment-col-slots',
    },
    {
      id: 'reference',
      header: 'Comprobante',
      accessor: (r) => r.reference,
      className: 'manual-payment-col-reference',
    },
    {
      id: 'amount',
      header: 'Valor',
      accessor: (r) => money(cents(r.amount)),
      className: 'manual-allocation__money manual-payment-col-value',
    },
    {
      id: 'status',
      header: 'Estado',
      className: 'manual-payment-col-status',
      accessor: (r) =>
        r.status === 'submitted'
          ? 'En revisión'
          : r.status === 'approved'
            ? 'Aprobado'
            : 'En preparación',
    },
    {
      id: 'action',
      header: 'Acción',
      className: 'manual-payment-col-action',
      accessor: () => '',
      cell: (r) => (
        <button
          className="ui-button ui-button--secondary manual-allocation__open"
          type="button"
          onClick={() => void select(r.id)}
          disabled={busy}
        >
          <ListChecks aria-hidden="true" />
          Distribuir
        </button>
      ),
    },
  ];
  const obligations = detail?.obligations ?? [];
  const suggestionModified =
    Boolean(detail) &&
    obligations.some((row) => cents(values[row.id] ?? '0') !== cents(row.suggestedAmount));
  const partial = obligations.find((row) => {
    const applied = cents(values[row.id] ?? '0');
    return applied > 0 && applied < cents(row.outstandingAmount);
  });
  type Row = (typeof obligations)[number];
  const allocationColumns: readonly DataColumn<Row>[] = [
    {
      id: 'selected',
      header: 'Aplicar',
      accessor: (r) => (cents(values[r.id] ?? '0') > 0 ? 'Sí' : 'No'),
      cell: (r) => {
        const checked = cents(values[r.id] ?? '0') > 0;
        return (
          <label className="manual-allocation__table-check">
            <input type="checkbox" checked={checked} onChange={() => toggle(r)} />
            <span className="sr-only">
              Aplicar a {r.conceptName} {r.period}
            </span>
          </label>
        );
      },
    },
    { id: 'period', header: 'Período', accessor: (r) => r.period },
    { id: 'concept', header: 'Concepto', accessor: (r) => r.conceptName },
    {
      id: 'pending',
      header: 'Pendiente',
      accessor: (r) => money(cents(r.outstandingAmount)),
      className: 'manual-allocation__money',
    },
    {
      id: 'result',
      header: 'Resultado',
      accessor: (r) => {
        const applied = cents(values[r.id] ?? '0'),
          pending = cents(r.outstandingAmount);
        return applied === 0
          ? 'Pendiente'
          : applied < pending
            ? `Abono parcial · faltan ${money(pending - applied)}`
            : 'Pago completo';
      },
      cell: (r) => {
        const applied = cents(values[r.id] ?? '0'),
          pending = cents(r.outstandingAmount),
          isPartial = applied > 0 && applied < pending;
        const stateClass =
          applied === 0
            ? 'manual-allocation__pending'
            : isPartial
              ? 'manual-allocation__partial'
              : 'manual-allocation__paid';
        return (
          <span className={stateClass}>
            {applied === 0
              ? 'Pendiente'
              : isPartial
                ? `Abono parcial · faltan ${money(pending - applied)}`
                : 'Pago completo'}
          </span>
        );
      },
    },
    {
      id: 'amount',
      header: 'Valor aplicado',
      accessor: (r) => values[r.id] ?? '',
      cell: (r) => {
        const checked = cents(values[r.id] ?? '0') > 0;
        return (
          <label className="manual-allocation__table-amount">
            <span className="sr-only">
              Valor aplicado a {r.conceptName} {r.period}
            </span>
            <span aria-hidden="true">$</span>
            <input
              type="number"
              min="0"
              max={r.outstandingAmount}
              step="0.01"
              inputMode="decimal"
              value={values[r.id] ?? ''}
              disabled={!checked}
              onChange={(event) =>
                setValues((current) => ({ ...current, [r.id]: event.target.value }))
              }
            />
          </label>
        );
      },
      className: 'manual-allocation__amount-column',
    },
  ];
  return (
    <section className="manual-allocation">
      <header>
        <span>Propuesta automática</span>
        <h2>Comprobantes disponibles</h2>
        <p>Selecciona un comprobante para revisar su propuesta automática de distribución.</p>
      </header>
      {error ? (
        <p className="member-payment__message member-payment__message--error" role="alert">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="manual-allocation__success" role="status">
          <CheckCircle2 />
          {success}
        </p>
      ) : null}
      <div className="manual-allocation__payments">
        <DataTable
          rows={items}
          columns={paymentColumns}
          visibleColumnIds={new Set(paymentColumns.map((c) => c.id))}
          sort={{ columnId: 'member', direction: 'ascending' }}
          onSort={() => {}}
          empty={
            <EmptyState
              title="Sin comprobantes disponibles"
              description="No existen comprobantes pendientes de distribución."
            />
          }
        />
      </div>
      <CanonicalModal
        open={Boolean(detail)}
        size="large"
        eyebrow="Revisión del comprobante"
        title={
          detail ? `Distribuir comprobante ${detail.payment.reference}` : 'Distribuir comprobante'
        }
        {...(detail ? { description: detail.payment.memberName } : {})}
        onClose={() => {
          setDetail(null);
          setValues({});
          setError('');
        }}
      >
        {detail ? (
          <div className="manual-allocation__modal">
            <strong className="manual-allocation__aliquot-badge">
              Alícuota {detail.payment.slotNumber ?? 'sin número'}
            </strong>
            {error ? (
              <p
                className="member-payment__message member-payment__message--error manual-allocation__modal-error"
                role="alert"
              >
                {error}
              </p>
            ) : null}
            <div className="manual-allocation__validation">
              <div className="manual-allocation__evidence">
                {evidencePreviewUrl ? (
                  detail.payment.evidenceMimeType === 'application/pdf' ? (
                    <iframe title="Comprobante bancario" src={evidencePreviewUrl} />
                  ) : (
                    <img src={evidencePreviewUrl} alt="Comprobante bancario registrado" />
                  )
                ) : evidencePreviewError ? (
                  <div className="manual-allocation__evidence-state" role="alert">
                    <strong>No se pudo mostrar el comprobante</strong>
                    <span>{evidencePreviewError}</span>
                    <button
                      type="button"
                      className="ui-button ui-button--secondary"
                      onClick={() => setEvidenceAttempt((value) => value + 1)}
                    >
                      Intentar nuevamente
                    </button>
                  </div>
                ) : (
                  <div className="manual-allocation__evidence-state" role="status">
                    <strong>Cargando comprobante…</strong>
                    <span>Estamos obteniendo el archivo protegido.</span>
                  </div>
                )}
              </div>
              <dl>
                <div>
                  <dt>Socio</dt>
                  <dd>{detail.payment.memberName}</dd>
                </div>
                <div>
                  <dt>Tipo de pago</dt>
                  <dd>{detail.payment.methodName}</dd>
                </div>
                <div>
                  <dt>Comprobante o referencia</dt>
                  <dd>{detail.payment.reference}</dd>
                </div>
                <div>
                  <dt>Valor</dt>
                  <dd>{money(payment)}</dd>
                </div>
                <div>
                  <dt>Fecha del comprobante</dt>
                  <dd>{longDate(detail.payment.transactionDate)}</dd>
                </div>
                <div>
                  <dt>Registrado</dt>
                  <dd>{longDateTime(detail.payment.registeredAt)}</dd>
                </div>
                <div>
                  <dt>Banco de origen</dt>
                  <dd>{detail.payment.originBank ?? 'No aplica'}</dd>
                </div>
                <div>
                  <dt>Cuenta receptora</dt>
                  <dd>{detail.payment.destinationAccount}</dd>
                </div>
                <div className="manual-allocation__observation">
                  <dt>Observación del socio</dt>
                  <dd>{detail.payment.memberObservation || 'Sin observación'}</dd>
                </div>
              </dl>
            </div>
            <section className="manual-allocation__suggestion">
              <div>
                <span>Propuesta automática</span>
                <strong>
                  {detail.suggestion.completeCount} cuotas completas ·{' '}
                  {detail.suggestion.partialCount} abono parcial
                </strong>
                <p>{detail.suggestion.explanation}</p>
              </div>
              <button
                type="button"
                className="ui-button ui-button--secondary"
                onClick={restoreSuggestion}
                disabled={!suggestionModified}
              >
                Restaurar sugerencia
              </button>
            </section>
            <div className="manual-allocation__summary">
              <div>
                <small>Comprobante</small>
                <strong>{money(payment)}</strong>
              </div>
              <div>
                <small>Asignado</small>
                <strong>{money(allocated)}</strong>
              </div>
              <div className={remaining < 0 ? 'is-error' : remaining > 0 ? 'has-balance' : ''}>
                <small>Crédito por generar</small>
                <strong>{money(remaining)}</strong>
              </div>
            </div>
            {success ? (
              <div className="manual-allocation__saved" role="status">
                <CheckCircle2 aria-hidden="true" />
                <div>
                  <strong>Propuesta registrada</strong>
                  <p>{success}</p>
                  <small>
                    La aprobación definitiva y el registro de cuotas pagadas se realizarán en la
                    confirmación del comprobante.
                  </small>
                </div>
              </div>
            ) : null}
            {partial ? (
              <p className="manual-allocation__balance-note">
                <strong>
                  Abono parcial automático: {money(cents(values[partial.id] ?? '0'))}.
                </strong>{' '}
                Se asigna a {partial.conceptName} del período {partial.period}; faltan{' '}
                {money(cents(partial.outstandingAmount) - cents(values[partial.id] ?? '0'))}. Esta
                cuota continuará como <strong>Pendiente</strong> hasta completar su valor total.
              </p>
            ) : remaining > 0 ? (
              <p className="manual-allocation__balance-note">
                <strong>{money(remaining)} se registrará como crédito disponible.</strong> No
                existen más obligaciones compatibles con el destino seleccionado y el valor no se
                aplicará silenciosamente.
              </p>
            ) : (
              <p className="manual-allocation__hint">
                El comprobante quedó distribuido completamente en cuotas pagadas. Puedes ajustar la
                propuesta antes de guardarla.
              </p>
            )}
            <div className="manual-allocation__table">
              <DataTable
                rows={obligations}
                columns={allocationColumns}
                visibleColumnIds={new Set(allocationColumns.map((c) => c.id))}
                sort={{ columnId: 'period', direction: 'ascending' }}
                onSort={() => {}}
                empty={
                  <EmptyState
                    title="Sin obligaciones pendientes"
                    description="Este socio no tiene obligaciones para el destino seleccionado."
                  />
                }
              />
            </div>
            <div className="manual-allocation__actions">
              <button
                className="ui-button ui-button--secondary"
                type="button"
                onClick={() => setDetail(null)}
              >
                Cancelar
              </button>
              <button
                className="ui-button ui-button--secondary"
                type="button"
                disabled={busy || remaining < 0}
                onClick={() => void save()}
              >
                {busy ? 'Guardando…' : 'Guardar propuesta'}
              </button>
              <button
                className="ui-button ui-button--danger"
                type="button"
                disabled={busy || !['submitted', 'under_review'].includes(detail.payment.status)}
                onClick={() => {
                  setRejectReason('');
                  setRejectOpen(true);
                }}
              >
                Rechazar
              </button>{' '}
              <button
                className="ui-button ui-button--primary"
                type="button"
                disabled={busy || remaining < 0}
                onClick={() => setConfirmOpen(true)}
              >
                Aprobar y aplicar
              </button>
            </div>
          </div>
        ) : null}
      </CanonicalModal>
      <CanonicalModal
        open={rejectOpen}
        size="medium"
        eyebrow="Decisión de revisión"
        title="Rechazar comprobante"
        description="El comprobante saldrá de la bandeja sin modificar cuotas ni saldos."
        onClose={() => !busy && setRejectOpen(false)}
      >
        <label className="member-payment__field">
          <span>Motivo del rechazo *</span>
          <textarea
            value={rejectReason}
            minLength={5}
            rows={4}
            onChange={(event) => setRejectReason(event.target.value)}
            placeholder="Ej.: el número de comprobante no coincide con el documento."
          />
        </label>
        <div className="manual-allocation__actions">
          <button
            className="ui-button ui-button--secondary"
            type="button"
            disabled={busy}
            onClick={() => setRejectOpen(false)}
          >
            Cancelar
          </button>
          <button
            className="ui-button ui-button--danger"
            type="button"
            disabled={busy || rejectReason.trim().length < 5}
            onClick={() => void reject()}
          >
            {busy ? 'Rechazando…' : 'Confirmar rechazo'}
          </button>
        </div>
      </CanonicalModal>{' '}
      <CanonicalModal
        open={confirmOpen}
        size="medium"
        eyebrow="Confirmación auditada"
        title="Aprobar y aplicar comprobante"
        description={
          'Se aplicará ' + money(allocated) + ' a las cuotas y el cambio quedará auditado.'
        }
        onClose={() => !busy && setConfirmOpen(false)}
      >
        <TextAreaField
          label="Observación de aprobación (opcional)"
          rows={4}
          maxLength={1000}
          value={approvalObservation}
          onChange={(event) => setApprovalObservation(event.target.value)}
          placeholder="Ej.: comprobante validado con el estado bancario."
          hint={`${approvalObservation.length}/1000 caracteres`}
        />
        <div className="manual-allocation__actions">
          <button
            className="ui-button ui-button--secondary"
            type="button"
            disabled={busy}
            onClick={() => setConfirmOpen(false)}
          >
            Cancelar
          </button>
          <button
            className="ui-button ui-button--primary"
            type="button"
            disabled={busy}
            onClick={() => void approve()}
          >
            {busy ? 'Aprobando…' : 'Confirmar aprobación'}
          </button>
        </div>
      </CanonicalModal>
      <CanonicalModal
        open={Boolean(receipt)}
        size="medium"
        eyebrow="Pago aplicado correctamente"
        title={
          receipt ? `Comprobante aprobado y aplicado · ${receipt.number}` : 'Resumen de aplicación'
        }
        description="Los saldos y estados de las cuotas ya fueron actualizados."
        onClose={() => setReceipt(null)}
      >
        {receipt ? (
          <div className="manual-allocation__approval-summary" role="status">
            <dl>
              <div>
                <dt>Socio</dt>
                <dd>{receipt.memberName}</dd>
              </div>
              <div>
                <dt>Comprobante</dt>
                <dd>{receipt.reference}</dd>
              </div>
              <div>
                <dt>Total aplicado</dt>
                <dd>USD {receipt.appliedAmount}</dd>
              </div>
              <div>
                <dt>Crédito disponible</dt>
                <dd>USD {receipt.creditAmount}</dd>
              </div>
            </dl>
            <div className="manual-allocation__approval-counts">
              <strong>
                {receipt.allocations.filter((item) => item.status === 'paid').length} cuotas pagadas
              </strong>
              <span>
                {receipt.allocations.filter((item) => item.status === 'partially_paid').length}{' '}
                abonos parciales
              </span>
            </div>
            <ul>
              {receipt.allocations.map((item, index) => (
                <li key={`${item.period}-${index}`}>
                  <span>
                    <strong>{item.period}</strong>
                    <small>{item.conceptName}</small>
                  </span>
                  <b>USD {item.appliedAmount}</b>
                  <em>{item.status === 'paid' ? 'Pagada' : 'Abono parcial'}</em>
                </li>
              ))}
            </ul>
            {deliveryMessage ? (
              <p className="manual-allocation__delivery-message" role="status">
                {deliveryMessage}
              </p>
            ) : null}
            <div className="manual-allocation__approval-actions">
              <a
                className="ui-button ui-button--secondary"
                href={`#socios/${receipt.memberId}?tab=payments`}
              >
                Ver pagos del socio
              </a>
              <button
                type="button"
                className="ui-button ui-button--secondary"
                onClick={() => {
                  setError('');
                  void openProtectedDocument(
                    paymentReceiptUrl(apiBase, receipt.id),
                    'download',
                    `${receipt.number}.png`,
                  ).catch((failure) =>
                    setError(
                      failure instanceof Error
                        ? failure.message
                        : 'No se pudo descargar el recibo.',
                    ),
                  );
                }}
              >
                Descargar recibo oficial
              </button>
              <button
                type="button"
                className="ui-button ui-button--secondary"
                disabled={deliveryBusy !== null}
                onClick={() => {
                  setDeliveryBusy('whatsapp');
                  setDeliveryMessage('');
                  void sharePaymentReceiptViaWhatsApp({
                    url: paymentReceiptUrl(apiBase, receipt.id),
                    receiptNumber: receipt.number,
                    memberName: receipt.memberName,
                    appliedAmount: receipt.appliedAmount,
                  })
                    .then((result) =>
                      setDeliveryMessage(
                        result === 'copied-and-opened'
                          ? 'Imagen copiada. WhatsApp est\u00e1 abierto: selecciona el chat y pega con Ctrl+V.'
                          : result === 'downloaded-and-opened'
                            ? 'Recibo descargado y WhatsApp abierto con el texto preparado. Adjunta el PNG descargado.'
                            : 'Recibo compartido con el selector del sistema.',
                      ),
                    )
                    .catch((failure) =>
                      setDeliveryMessage(
                        failure instanceof Error
                          ? failure.message
                          : 'No se pudo compartir el recibo.',
                      ),
                    )
                    .finally(() => setDeliveryBusy(null));
                }}
              >
                <Share2 aria-hidden="true" />
                {deliveryBusy === 'whatsapp' ? 'Preparando\u2026' : 'Compartir por WhatsApp'}
              </button>
              <button
                type="button"
                className="ui-button ui-button--primary"
                disabled={deliveryBusy !== null}
                onClick={() => {
                  setDeliveryBusy('email');
                  setDeliveryMessage('');
                  void emailPaymentReceipt(apiBase, receipt.id)
                    .then((result) => setDeliveryMessage(`Recibo enviado a ${result.recipient}.`))
                    .catch((failure) =>
                      setDeliveryMessage(
                        failure instanceof Error
                          ? failure.message
                          : 'No se pudo enviar el recibo por correo.',
                      ),
                    )
                    .finally(() => setDeliveryBusy(null));
                }}
              >
                <Mail aria-hidden="true" />
                {deliveryBusy === 'email' ? 'Enviando\u2026' : 'Enviar por correo'}
              </button>
            </div>
          </div>
        ) : null}
      </CanonicalModal>
    </section>
  );
}
